export interface DiscordVoiceStatus {
    success: boolean;
    guildId?: string;
    guildName?: string;
    voiceChannelId?: string;
    voiceChannelName?: string;
}

export class DiscordBotApi {
    private static readonly BASE_URL = "https://torresowo.myftp.org:7777/spotip17an-discord/api";

    /**
     * Checks if a Discord user is currently connected to a voice channel.
     * @param userId The Discord user ID
     */
    static async getUserVoiceStatus(userId: string): Promise<DiscordVoiceStatus> {
        const url = `${this.BASE_URL}/user/${userId}/voice`;
        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP Error: ${response.status} ${response.statusText}`);
            }
            return await response.json() as DiscordVoiceStatus;
        } catch (error) {
            console.error("Error in DiscordBotApi.getUserVoiceStatus:", error);
            throw error;
        }
    }

    /**
     * Obtains the current playback state of the bot for the specified guild.
     */
    static async getCurrentState(guildId: string): Promise<any> {
        const url = `${this.BASE_URL}/${guildId}/current`;
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`HTTP Error: ${response.status} ${response.statusText}`);
        }
        return await response.json();
    }

    /**
     * Retrieves the current playback queue page.
     */
    static async getQueue(guildId: string, page: number = 1, textChannelId?: string): Promise<any> {
        let url = `${this.BASE_URL}/${guildId}/queue?page=${page}`;
        if (textChannelId) {
            url += `&textChannelId=${encodeURIComponent(textChannelId)}`;
        }
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`HTTP Error: ${response.status} ${response.statusText}`);
        }
        return await response.json();
    }

    /**
     * Plays or enqueues a song, playlist, or artist URL on the bot.
     */
    static async play(guildId: string, url: string, voiceChannelId?: string, textChannelId?: string): Promise<any> {
        return this.post(`/${guildId}/play`, { url, voiceChannelId, textChannelId });
    }

    /**
     * Pauses the current playback.
     */
    static async pause(guildId: string, textChannelId?: string): Promise<any> {
        return this.post(`/${guildId}/pause`, { textChannelId });
    }

    /**
     * Resumes the paused playback.
     */
    static async resume(guildId: string, textChannelId?: string): Promise<any> {
        return this.post(`/${guildId}/resume`, { textChannelId });
    }

    /**
     * Skips to the next song in the queue.
     */
    static async next(guildId: string, textChannelId?: string): Promise<any> {
        return this.post(`/${guildId}/next`, { textChannelId });
    }

    /**
     * Stops the playback and clears the queue.
     */
    static async stop(guildId: string, textChannelId?: string): Promise<any> {
        return this.post(`/${guildId}/stop`, { textChannelId });
    }

    /**
     * Forces the bot to leave the voice channel.
     */
    static async leave(guildId: string, textChannelId?: string): Promise<any> {
        return this.post(`/${guildId}/leave`, { textChannelId });
    }

    /**
     * Clears the current playback queue.
     */
    static async cleanQueue(guildId: string, textChannelId?: string): Promise<any> {
        return this.post(`/${guildId}/clean`, { textChannelId });
    }

    /**
     * Shuffles the current queue.
     */
    static async shuffle(guildId: string, textChannelId?: string): Promise<any> {
        return this.post(`/${guildId}/shuffle`, { textChannelId });
    }

    /**
     * Sets the loop mode (none, queue, song).
     */
    static async loop(guildId: string, loopMode: 'none' | 'queue' | 'song', textChannelId?: string): Promise<any> {
        return this.post(`/${guildId}/loop`, { loopMode, textChannelId });
    }

    /**
     * Executes a generic bot command with arguments.
     */
    static async genericCommand(guildId: string, command: string, args: string[], voiceChannelId?: string, textChannelId?: string): Promise<any> {
        return this.post(`/${guildId}/command`, { command, args, voiceChannelId, textChannelId });
    }

    /**
     * Helper post request method.
     */
    private static async post(endpoint: string, body?: any): Promise<any> {
        const response = await fetch(`${this.BASE_URL}${endpoint}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: body ? JSON.stringify(body) : undefined
        });
        if (!response.ok) {
            throw new Error(`HTTP Error: ${response.status} ${response.statusText}`);
        }
        return await response.json();
    }
}
